'use client';
import type { ComponentProps } from 'react';
import { clsx } from 'clsx';
import Image from 'next/image';
import { Dialog } from 'radix-ui';
import { FallbackImage } from 'components/UI/FallbackImage';
import Link from 'components/UI/Link/Link';
import { useEpgProgress } from 'hooks/useEpgProgress';
import { slugify } from 'utils/string';
import type { EpgItemProps } from '../EpgItem';
import styles from './DetailView.module.scss';

type DetailViewProps = EpgItemProps & ComponentProps<'div'> & { readonly isDialog?: boolean };

export default function DetailView({
  id,
  startDate,
  endDate,
  name,
  subtitle,
  description,
  image,
  link,
  rtlPlusLink,
  hasUT,
  isDialog,
  className,
  ...props
}: DetailViewProps) {
  const { progressValue, progressMax, remainingLabel, isPlaying } = useEpgProgress(startDate, endDate);

  const startDateLabel = new Intl.DateTimeFormat('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(startDate));

  return (
    <div className={clsx(styles.root, { [styles.isDialog]: isDialog }, className)} {...props}>
      <div className={styles.time}>{startDateLabel}</div>
      <div className={styles.content}>
        {!!image && <Image className={styles.image} src={image} width={711} height={400} alt={name} unoptimized />}
        {!image && <FallbackImage className={styles.image} />}
        <div>
          {isDialog ? (
            <Dialog.Title asChild>
              <div className={styles.title}>{name}</div>
            </Dialog.Title>
          ) : (
            <div className={styles.title}>{name}</div>
          )}
          {(!!subtitle || hasUT) && (
            <span className={styles.subtitle}>
              {hasUT && <span className={styles.ut}>UT</span>}
              {subtitle}
            </span>
          )}
          {!!description && <p className={styles.description}>{description}</p>}
        </div>
      </div>
      <div>
        {isPlaying && (
          <>
            <progress
              className={styles.progress}
              value={progressValue}
              max={progressMax}
              aria-live="polite"
              aria-describedby={`item-${id}-remaining-text`}
            />
            <p id={`item-${id}-remaining-text`} className={styles.remainingText}>
              {remainingLabel}
            </p>
          </>
        )}
      </div>
      <div className={styles.links}>
        {!!link && (
          <Link className={styles.buttonLink} href={link} aria-label={`Alles zur Sendung: ${name}`}>
            Alles zur Sendung
          </Link>
        )}
        {!!rtlPlusLink && !isPlaying && (
          <Link
            className={styles.buttonLink}
            href={rtlPlusLink}
            target="_blank"
            aria-label={`${name} auf RTL+ ansehen`}
          >
            Auf <strong>RTL+</strong> ansehen
          </Link>
        )}
        {isPlaying && (
          <Link
            className={styles.buttonLink}
            href={`https://plus.rtl.de/video-tv/live-tv/rtl-1?utm_source=rtl&utm_medium=owned&utm_campaign=teaser_tv-programm&utm_term=${slugify(name)}`}
            target="_blank"
            aria-label={`${name} jetzt auf RTL+ streamen`}
          >
            Jetzt live <strong>auf RTL+</strong> streamen
          </Link>
        )}
      </div>
    </div>
  );
}
